/* CrossDivisionRail — slim "Complete the experience" rail rendered at the
 * bottom of a Services PDP. Surfaces the most-relevant Systems service +
 * Services product + Academy program based on shared keywords from the
 * unified search index (`/api/recommendations/related`).
 *
 * Designed to convert single-purchase visitors into multi-division
 * engagement: someone buying an iPhone here ALSO sees ZHELTA Systems'
 * mobile-device deployment and an Academy program for iOS development.
 *
 * Rendered as a 3-column responsive grid (1 col mobile, 3 col md+). Each
 * card carries the division-coloured icon halo + label. Cards link to
 * the right native page so the visit stays inside the monorepo.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, ShoppingBag, GraduationCap, ArrowRight, Sparkles, Flame } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const KIND_META = {
  systems_service: { label: 'ZHELTA Systems', sub: 'Enterprise IT', Icon: Cpu, accent: '#5b8def' },
  services_product: { label: 'ZHELTA Services', sub: 'Verified hardware', Icon: ShoppingBag, accent: '#c9a227' },
  academy_program: { label: 'ZHELTA Academy', sub: 'Practical training', Icon: GraduationCap, accent: '#a875ff' },
};

export default function CrossDivisionRail({ slug, kind = 'services_product' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { format } = useCurrency();

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/recommendations/related?slug=${encodeURIComponent(slug)}&kind=${encodeURIComponent(kind)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) { setData(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [slug, kind]);

  // Fire-and-forget click log so the Admin "Top cross-division paths"
  // widget can rank which source→target combinations are converting.
  const logClick = (targetKind, targetSlug) => {
    try {
      fetch(`${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api/recommendations/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_kind: kind, source_slug: slug,
          target_kind: targetKind, target_slug: targetSlug,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* ignore */ }
  };

  // Render at most 3 cards, in a deterministic order so the layout doesn't
  // shimmer between PDPs even when one division has no match.
  const cards = data
    ? [
        { kind: 'systems_service',  row: data.systems  },
        { kind: 'services_product', row: data.services },
        { kind: 'academy_program',  row: data.academy  },
      ].filter((c) => c.row)
    : [];

  if (loading) {
    return (
      <section className="zh-container py-10" data-testid="cross-division-rail-loading">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-2xl border border-gray-200 bg-white h-[150px] animate-pulse" />
          ))}
        </div>
      </section>
    );
  }
  if (cards.length === 0) return null;

  return (
    <section className="zh-container py-12" data-testid="cross-division-rail">
      <div className="flex items-center gap-2 mb-5">
        <Sparkles className="w-3.5 h-3.5 text-zinc-700" />
        <span className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-zinc-800">
          Complete the experience · across all 3 ZHELTA divisions
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cross-division-rail-grid">
        {cards.map(({ kind: k, row }) => {
          const meta = KIND_META[k];
          const Icon = meta.Icon;
          return (
            <Link
              key={k}
              to={row.url}
              onClick={() => logClick(k, row.slug)}
              data-testid={`cross-division-card-${k}`}
              className="group rounded-2xl border border-gray-200 bg-white p-5 hover:border-[#c9a227] hover:shadow-md transition-all flex flex-col"
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="h-10 w-10 rounded-full grid place-items-center shrink-0"
                  style={{ background: `${meta.accent}1a`, color: meta.accent }}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <span className="flex flex-col leading-tight flex-1 min-w-0">
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.18em]" style={{ color: meta.accent }}>{meta.label}</span>
                  <span className="text-[11px] text-gray-500">{meta.sub}</span>
                </span>
                {row.boost > 0 && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider shrink-0"
                    style={{ background: '#fef3c7', color: '#a8871a' }}
                    title={`Trending — chosen by ${row.boost} other ${row.boost === 1 ? 'visitor' : 'visitors'} who came from this page`}
                    data-testid={`cross-division-card-${k}-trending`}
                  >
                    <Flame className="w-2.5 h-2.5" /> Trending
                  </span>
                )}
              </div>
              <h3 className="text-[15px] font-bold text-gray-900 mb-1.5 group-hover:text-zinc-700 transition-colors">{row.name}</h3>
              {row.tagline && (
                <p className="text-[12.5px] text-gray-500 leading-snug line-clamp-3 flex-1">{row.tagline}</p>
              )}
              <div className="mt-3 flex items-center justify-between">
                {k === 'services_product' && typeof row.price === 'number' && row.price > 0 ? (
                  <span className="text-[13.5px] font-bold text-gray-900">{format(row.price)}</span>
                ) : (
                  <span className="text-[11px] uppercase tracking-wider text-gray-400">{row.category || 'Learn more'}</span>
                )}
                <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-gray-500 group-hover:text-zinc-700 transition-colors">
                  Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
